"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  applySaasThemeToDocument,
  normalizeStoredTheme,
  resolveSaasTheme,
  SAAS_THEME_PREFERENCES,
  SAAS_THEME_STORAGE_KEY,
  subscribeSystemTheme,
  type SaasTheme,
  type SaasThemePreference,
} from "./appearance.js";

type ThemeContextValue = {
  /** Theme ĐANG áp vào DOM — luôn là giá trị cụ thể, không bao giờ `"system"`. */
  theme: SaasTheme;
  /** Lựa chọn của người dùng — có thể là `"system"`. */
  preference: SaasThemePreference;
  setPreference: (preference: SaasThemePreference) => void;
  /** Alias của `setPreference`, giữ để call site cũ không phải sửa. */
  setTheme: (preference: SaasThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function SaasThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = SAAS_THEME_STORAGE_KEY,
}: {
  children: ReactNode;
  defaultTheme?: SaasThemePreference;
  storageKey?: string;
}) {
  const [preference, setPreferenceState] = useState<SaasThemePreference>(defaultTheme);
  const [theme, setThemeState] = useState<SaasTheme>(() => resolveSaasTheme(defaultTheme));
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setPreferenceState(normalizeStoredTheme(localStorage.getItem(storageKey), defaultTheme));
  }, [defaultTheme, storageKey]);

  useEffect(() => {
    if (!mounted) return;
    localStorage.setItem(storageKey, preference);

    const apply = () => {
      const next = resolveSaasTheme(preference);
      setThemeState(next);
      applySaasThemeToDocument(document.documentElement, next);
    };
    apply();

    // Chỉ preference "system" mới phải theo dõi OS. Các preference khác là giá trị tuyệt đối.
    if (preference !== "system") return;
    return subscribeSystemTheme(apply);
  }, [preference, mounted, storageKey]);

  const setPreference = useCallback((next: SaasThemePreference) => {
    if (!(SAAS_THEME_PREFERENCES as readonly string[]).includes(next)) return;
    setPreferenceState(next);
  }, []);

  const value = useMemo(
    () => ({ theme, preference, setPreference, setTheme: setPreference }),
    [theme, preference, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useSaasTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useSaasTheme must be used within SaasThemeProvider");
  return ctx;
}
